import { useMutation } from "@tanstack/react-query";
import { apiClient } from "~/lib/client";
import { handleError, handleResponse } from "~/utils/response";
import { getQueryClient } from "~/providers/query.provider";
import { toast } from "sonner";

// Define types
export interface RegistrationPaymentFormData {
  name: string;
  email?: string;
  phone?: string;
  success_url?: string;
  cancel_url?: string;
  consultation_id?: string;
}

export interface RegistrationPaymentResponseData {
  id: string;
  payment_metadata: {
    url: string;
    amount: number;
    currency: string;
    payment_id: string;
  };
}

export interface RegistrationPaymentResponse {
  success: boolean;
  data?: RegistrationPaymentResponseData;
  error?: string | null;
}

// Define query keys
export const registrationKeys = {
  all: ['registrations'] as const,
  payments: () => [...registrationKeys.all, 'payments'] as const,
};

// Create mutation hook
export const useCreateRegistrationPaymentMutation = () => {
  const queryClient = getQueryClient();

  return useMutation<RegistrationPaymentResponse, unknown, RegistrationPaymentFormData>({
    mutationFn: async (variables: RegistrationPaymentFormData) => {
      try {
        const response = await apiClient("/consultations/payment", {
          method: "POST",
          body: JSON.stringify(variables),
        });

        return await handleResponse<RegistrationPaymentResponseData>(response);
      } catch (error) {
        return handleError(error);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: registrationKeys.payments() });
    },
    onError: () => {
      toast.error("Failed to process registration payment");
    },
  });
};